//@ts-check
import "../../styles/SharePointPermissionsForm.css";

import React, { useMemo, useState } from "react";
import env from "react-dotenv";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../../auth/authConfig";

import { FormGroup, Label, Input, Alert } from "reactstrap";
import { Card, Spinner, Button, Form, Modal } from "react-bootstrap";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faPlus, faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";

/**
 * Normalize API response so we can reliably render a list of permissions.
 * Supports:
 *  - sitePermissions: object
 *  - sitePermissions: array
 *  - sitePermissions missing/null
 */
function normalizePermissions(sitePermissions) {
  if (!sitePermissions) return [];
  if (Array.isArray(sitePermissions)) return sitePermissions;
  return [sitePermissions];
}

function isGuid(value) {
  // Accepts standard GUID format
  const guidRegex =
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  return guidRegex.test((value || "").trim());
}

export const SharePointPermissionsForm = () => {
  const [siteIdentifier, setSiteIdentifier] = useState("");
  const [loading, setLoading] = useState(false);
  const [locked, setLocked] = useState(false);

  const [message, setMessage] = useState(/** @type {null | {text: string, type: string}} */ (null));

  const [siteInfo, setSiteInfo] = useState(
    /** @type {null | {
     *  siteName?: string,
     *  siteWebUrl?: string,
     *  siteDisplayName?: string,
     *  siteId?: string,
     *  sitePermissions?: any
     * }} */ (null)
  );

  // Add Permission modal state
  const [showAddModal, setShowAddModal] = useState(false);

  // MINIMAL CHANGE: rename newAppId -> newAppIdentifier
  const [newAppIdentifier, setNewAppIdentifier] = useState("");
  const [newRole, setNewRole] = useState("read");

  // MINIMAL CHANGE: no longer require GUID; only require non-empty
  const [isValidNewAppIdentifier, setIsValidNewAppIdentifier] = useState(false);

  const { instance, accounts } = useMsal();

  const baseUrl = env.API_BASE_URL || "http://localhost:7071";

  const permissions = useMemo(() => normalizePermissions(siteInfo?.sitePermissions), [siteInfo]);

  const handleSubmit = async (event) => {
    setLoading(true);
    try {
      if (event) event.preventDefault();

      setMessage(null);
      setSiteInfo(null);

      if (!siteIdentifier || !siteIdentifier.trim()) {
        setMessage({ type: "warning", text: "Please enter a SharePoint Site ID or Site Name." });
        return;
      }

      const tokenResponse = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });

      const url = `${baseUrl}/api/sharepoint/sites/${encodeURIComponent(siteIdentifier.trim())}/permissions`;

      let response;
      try {
        response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${tokenResponse.accessToken}`,
          },
        });
      } catch (e) {
        console.error(e);
        setMessage({ type: "danger", text: "Error communicating with server" });
        return;
      }

      if (response.status === 200) {
        const json = await response.json();

        // Ensure sitePermissions is always an array for rendering + updates
        setSiteInfo({
          ...json,
          sitePermissions: normalizePermissions(json.sitePermissions),
        });

        setLocked(true);
        setMessage(null);
      } else if (response.status === 400) {
        setMessage({ type: "warning", text: await response.text() });
      } else if (response.status === 403) {
        setMessage({ type: "danger", text: "You do not have authorization to do that." });
      } else if (response.status === 404) {
        const notFoundMessage = await response.text();
        setMessage({ type: "warning", text: notFoundMessage || "Site not found." });
      } else {
        const err = await response.text();
        setMessage({ type: "danger", text: err || "An error occurred." });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSiteIdentifier("");
    setSiteInfo(null);
    setMessage(null);
    setLocked(false);
    setShowAddModal(false);

    // MINIMAL CHANGE: reset newAppIdentifier
    setNewAppIdentifier("");

    setNewRole("read");

    // MINIMAL CHANGE: reset validity
    setIsValidNewAppIdentifier(false);
  };

  // ---------------------------
  // DELETE Permission (Trash)
  // ---------------------------
  const handleConfirmRemovePermission = (permissionId) => {
    const shouldRemove = window.confirm("Are you sure you want to remove this permission?");
    if (shouldRemove) {
      handleRemovePermission(permissionId);
    }
  };

  const handleRemovePermission = async (permissionId) => {
    if (!permissionId) {
      setMessage({ type: "warning", text: "This permission is missing an Id and cannot be deleted." });
      return;
    }

    setLoading(true);
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });

      const siteId = siteInfo?.siteId;
      if (!siteId) {
        setMessage({ type: "danger", text: "Missing siteId from response; cannot delete permission." });
        return;
      }

      const deleteUrl = `${baseUrl}/api/sharepoint/sites/${encodeURIComponent(siteId)}/permissions/${encodeURIComponent(permissionId)}`;

      let response;
      try {
        response = await fetch(deleteUrl, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${tokenResponse.accessToken}`,
          },
        });
      } catch (e) {
        console.error(e);
        setMessage({ type: "danger", text: "Error communicating with server" });
        return;
      }

      if (response.status === 200 || response.status === 204) {
        // Remove from UI immediately
        setSiteInfo((prev) => {
          if (!prev) return prev;
          const prevPerms = Array.isArray(prev.sitePermissions) ? prev.sitePermissions : [];
          const remaining = prevPerms.filter((p) => p.Id !== permissionId);
          return { ...prev, sitePermissions: remaining };
        });

        setMessage({ type: "success", text: "Permission removed successfully." });
      } else if (response.status === 400) {
        setMessage({ type: "warning", text: await response.text() });
      } else if (response.status === 403) {
        setMessage({ type: "danger", text: "You do not have authorization to do that." });
      } else {
        const err = await response.text();
        setMessage({ type: "danger", text: err || "Failed to delete permission." });
      }
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------
  // ADD Permission (POST)
  // ---------------------------
  const openAddModal = () => {
    // MINIMAL CHANGE
    setNewAppIdentifier("");
    setNewRole("read");
    setIsValidNewAppIdentifier(false);
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
  };

  // MINIMAL CHANGE: accept id/appId/displayName, only validate non-empty
  const handleNewAppIdentifierChange = (e) => {
    const value = e.target.value;
    setNewAppIdentifier(value);
    setIsValidNewAppIdentifier(!!(value && value.trim().length > 0));
  };

  const handleAddPermission = async () => {
    setLoading(true);
    try {
      setMessage(null);

      const siteId = siteInfo?.siteId;
      if (!siteId) {
        setMessage({ type: "danger", text: "Missing siteId from response; cannot add permission." });
        return;
      }

      // MINIMAL CHANGE: no GUID requirement anymore
      if (!newAppIdentifier || !newAppIdentifier.trim()) {
        setMessage({ type: "warning", text: "App Identifier is required." });
        return;
      }

      if (!newRole || !newRole.trim()) {
        setMessage({ type: "warning", text: "Role is required." });
        return;
      }

      const tokenResponse = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });

      const postUrl = `${baseUrl}/api/sharepoint/sites/${encodeURIComponent(siteId)}/permissions`;

      let response;
      try {
        response = await fetch(postUrl, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${tokenResponse.accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            // MINIMAL CHANGE: send appIdentifier instead of AppId
            AppIdentifier: newAppIdentifier.trim(),
            Role: newRole.trim(),
          }),
        });
      } catch (e) {
        console.error(e);
        setMessage({ type: "danger", text: "Error communicating with server" });
        return;
      }

      if (response.status === 200 || response.status === 201) {
        const created = await response.json();

        // Add to UI immediately (avoid duplicates by Id if already present)
        setSiteInfo((prev) => {
          if (!prev) return prev;
          const prevPerms = Array.isArray(prev.sitePermissions) ? prev.sitePermissions : [];
          const normalizedCreated = created ? normalizePermissions(created) : [];
          const merged = [...prevPerms];

          for (const item of normalizedCreated) {
            if (!item?.Id || !merged.some((p) => p.Id === item.Id)) {
              merged.push(item);
            }
          }

          return { ...prev, sitePermissions: merged };
        });

        setMessage({ type: "success", text: "Permission added successfully." });
        closeAddModal();
      } else if (response.status === 400) {
        setMessage({ type: "warning", text: await response.text() });
      } else if (response.status === 403) {
        setMessage({ type: "danger", text: "You do not have authorization to do that." });
      } else {
        const err = await response.text();
        setMessage({ type: "danger", text: err || "Failed to add permission." });
      }
    } finally {
      setLoading(false);
    }
  };


  const PermissionCards = (perms) => {
    return (
      <div style={{ marginTop: "1em" }}>
        {perms.map((p) => {
          const hasSitesSelected = p?.HasSitesSelected === true;
          return (
            <Card
              className="spPermissionCard"
              key={p.Id || `${p.AppId}-${p.Roles}`}
              style={{ padding: ".85em" }}
            >
            <Card.Title className="spPermissionTitle">
              <span>
              {p.AppName || "Unknown App"}
              <a
                href={`https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationMenuBlade/~/Overview/appId/${p.AppId}`}
                target="_blank"
                rel="noopener noreferrer"
                title="Open app registration"
              >
                <FontAwesomeIcon
                  icon={faExternalLinkAlt}
                  className="open-app-icon"
                />
              </a>
              </span>


              <FontAwesomeIcon
                icon={faTrash}
                onClick={() => !loading && handleConfirmRemovePermission(p.Id)}
                className={`trash-icon ${loading ? "trash-icon-disabled" : ""}`}
                title="Remove permission"
              />
            </Card.Title>

              <Card.Body className="spPermissionBody">
                {p.AppId && (
                  <div className="spRow">
                    <span className="spLabel">AppId:</span>
                    <span className="spValue">{p.AppId}</span>
                  </div>
                )}

                <div className="spRow">
                  <span className="spLabel">Role(s):</span>
                  <span className="spValue">{p.Roles || "None"}</span>
                </div>



                {/* Red note if Sites.Selected app permissions are NOT present */}
                {!hasSitesSelected && (
                  <div
                    style={{
                      marginTop: "8px",
                      paddingTop: "6px",
                      // borderTop: "1px dashed rgba(220,53,69,.35)",
                      color: "#dc3545",
                      fontSize: ".9em",
                      lineHeight: 1.2,
                    }}
                  >
                    * This app does not have the <code>Sites.Selected</code> application permission.
                  </div>
                )}
              </Card.Body>
            </Card>
          );
        })}
      </div>
    );
  };


  return (
    <div>
      <div className="formHeader">SharePoint Permissions Lookup</div>

      <div className="SharePointPermissionsForm">
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label for="siteIdentifier">Site Identifier</Label>
            <Input
              type="text"
              id="siteIdentifier"
              value={siteIdentifier}
              onChange={(e) => setSiteIdentifier(e.target.value)}
              disabled={loading || locked}
              placeholder="Enter the site id (GUID) or site name"
            />
          </FormGroup>

          <Button
            disabled={loading || locked}
            type="submit"
            className="button"
            style={{ width: "200px", height: "38px", margin: "2px" }}
          >
            {loading ? <Spinner animation="border" size="sm" /> : "Get Site Permissions"}
          </Button>

          <Button
            className="button"
            style={{ width: "auto", height: "38px", margin: "2px" }}
            onClick={handleClear}
            type="button"
            disabled={loading}
          >
            Clear Search
          </Button>
        </Form>

        {message && (
          <div className="mt-3">
            <Alert color={message.type}>{message.text}</Alert>
          </div>
        )}

        {siteInfo && (
          <div style={{ marginTop: "1em" }}>
            <Card className="spSiteCard" style={{ padding: ".85em" }}>
              <Card.Title style={{ marginBottom: ".5em" }}>{siteInfo.siteDisplayName || "Site"}</Card.Title>
              <Card.Body style={{ marginTop: "-8px", marginBottom: "-8px" }}>
                {siteInfo.siteName && (
                  <div className="spRow">
                    <span className="spLabel">Site Name:</span>
                    <span className="spValue">{siteInfo.siteName}</span>
                  </div>
                )}
                {siteInfo.siteId && (
                  <div className="spRow">
                    <span className="spLabel">Site Id:</span>
                    <span className="spValue">{siteInfo.siteId}</span>
                  </div>
                )}
                {siteInfo.siteWebUrl && (
                  <div className="spRow">
                    <span className="spLabel">Web URL:</span>
                    <span className="spValue">
                      <a href={siteInfo.siteWebUrl} target="_blank" rel="noreferrer">
                        {siteInfo.siteWebUrl}
                      </a>
                    </span>
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* Actions row */}
            <div className="spActionsRow">
              <Label style={{ margin: "0" }}>Service Principals with Permissions: {permissions.length}</Label>

              <Button size="sm" className="spAddButton" onClick={openAddModal} disabled={loading} type="button">
                <FontAwesomeIcon icon={faPlus} style={{ marginRight: "6px" }} />
                Add Permission
              </Button>
            </div>

            {permissions.length > 0 ? (
              <div>{PermissionCards(permissions)}</div>
            ) : (
              <div style={{ marginTop: ".75em", opacity: 0.8 }}>No service principal permissions returned for this site.</div>
            )}
          </div>
        )}

        {/* Add Permission Modal */}
        <Modal centered show={showAddModal} onHide={closeAddModal}>
          <Modal.Header closeButton>
            <Modal.Title>Add Service Principal Permission</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                {/* MINIMAL CHANGE: label + placeholder + help text */}
                <Form.Label>App Identifier</Form.Label>
                <Form.Control
                  type="text"
                  value={newAppIdentifier}
                  onChange={handleNewAppIdentifierChange}
                  placeholder="Enter the appId, id, or display name"
                  disabled={loading}
                />
                <Form.Text className={isValidNewAppIdentifier ? "spHelpOk" : "spHelpWarn"}>
                  {newAppIdentifier.trim().length === 0
                    ? "Enter the service principal appId, id, or display name."
                    : isGuid(newAppIdentifier)
                      ? "Looks like a GUID — we'll try id first, then appId."
                      : "Looks like a string — we'll search by display name."}
                </Form.Text>
              </Form.Group>

              <Form.Group>
                <Form.Label>Role</Form.Label>
                <Form.Select value={newRole} onChange={(e) => setNewRole(e.target.value)} disabled={loading}>
                  <option value="read">read</option>
                  <option value="write">write</option>
                  <option value="fullcontrol">fullcontrol</option>
                  <option value="owner">owner</option>
                </Form.Select>
              </Form.Group>
            </Form>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={closeAddModal} disabled={loading}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleAddPermission} disabled={loading || !isValidNewAppIdentifier}>
              {loading ? <Spinner animation="border" size="sm" /> : "Submit"}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};
